import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Plus, Trash2, Globe, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import ProPortalHeader from '@/components/ProPortalHeader';
import { InstallerLeadFormView } from '@/components/embed/InstallerLeadFormView';
import { LEAD_SECTORS } from '@/config/leadSectors';
import { formatApiError } from '@/utils/apiError';

const SETTING_KEYS = ['business_name', 'headline', 'intro', 'button_color', 'submit_label', 'thank_you_title', 'thank_you_body', 'show_postcode', 'show_message', 'show_services', 'services'];

export default function InstallerLeadFormEditor() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/lead-form/settings`, { credentials: 'include' })
      .then((r) => r.json()).then(setCfg)
      .catch(() => toast.error('Could not load your contact form settings.'));
  }, []);

  const update = (patch) => { setCfg((c) => ({ ...c, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(SETTING_KEYS.map((k) => [k, cfg[k]]));
      const res = await fetch(`${API_URL}/api/lead-form/settings`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data) || 'Save failed');
      setCfg((c) => ({ ...c, ...data }));
      setDirty(false);
      toast.success('Contact form saved — your live form updates instantly.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm('Generate a new link? The old embed code on your website will stop working until you replace it.')) return;
    const res = await fetch(`${API_URL}/api/lead-form/regenerate-token`, { method: 'POST', credentials: 'include' });
    const data = await res.json();
    setCfg((c) => ({ ...c, token: data.token }));
    toast.success('New link generated — copy the new embed code.');
  };

  if (!cfg) return <div className="min-h-screen bg-[#E8ECEF]"><ProPortalHeader activeSection="toolbox" /><div className="p-10 text-center text-slate-500">Loading…</div></div>;

  const url = `${window.location.origin}/embed/lead-form/${cfg.token}`;
  const snippet = `<iframe\n  src="${url}"\n  title="Contact ${cfg.business_name || 'us'}"\n  style="width:100%;min-height:${cfg.show_services ? 820 : 640}px;border:0;"\n  loading="lazy"\n></iframe>`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(snippet); setCopied(true); toast.success('Embed code copied'); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error('Couldn\u2019t copy — select the text manually.'); }
  };

  return (
    <div className="min-h-screen bg-[#E8ECEF]">
      <ProPortalHeader activeSection="toolbox" />
      <main className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <button onClick={() => navigate('/toolbox')} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-3" data-testid="lead-form-back">
          <ArrowLeft className="w-4 h-4" /> Toolbox
        </button>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-[#27292E] flex items-center gap-2"><Globe className="w-7 h-7 text-[#006400]" /> Website Contact Form</h1>
            <p className="text-slate-600 mt-1">Paste this form on your own website — every enquiry lands straight in your Lead CRM and you get notified.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-bold">Leads via this form</div>
              <div className="text-2xl font-bold text-[#27292E]" data-testid="lead-form-submissions">{cfg.submissions ?? 0}</div>
            </div>
            <Button onClick={save} disabled={saving || !dirty} className="h-11 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="lf-save-top">
              {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <Card className="p-5 space-y-4">
              <h2 className="font-bold text-slate-800">Wording</h2>
              <Text label="Business name shown on form" value={cfg.business_name} onChange={(v) => update({ business_name: v })} testid="lf-business-name" />
              <Text label="Headline" value={cfg.headline} onChange={(v) => update({ headline: v })} testid="lf-headline" />
              <Text label="Intro line" value={cfg.intro} onChange={(v) => update({ intro: v })} testid="lf-intro" />
              <div className="grid grid-cols-2 gap-3">
                <Text label="Button text" value={cfg.submit_label} onChange={(v) => update({ submit_label: v })} testid="lf-submit-label" />
                <div>
                  <Label className="text-xs font-semibold text-slate-600">Button colour</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={cfg.button_color} onChange={(e) => update({ button_color: e.target.value })} className="h-10 w-14 rounded border cursor-pointer" data-testid="lf-button-color" />
                    <Input value={cfg.button_color} onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && update({ button_color: e.target.value })} className="font-mono" data-testid="lf-button-color-hex" />
                  </div>
                </div>
              </div>
              <Text label="Thank-you title" value={cfg.thank_you_title} onChange={(v) => update({ thank_you_title: v })} testid="lf-thanks-title" />
              <Text label="Thank-you message" value={cfg.thank_you_body} onChange={(v) => update({ thank_you_body: v })} testid="lf-thanks-body" />
            </Card>

            <Card className="p-5 space-y-3">
              <h2 className="font-bold text-slate-800">Fields</h2>
              <Toggle label="Ask for postcode" checked={cfg.show_postcode} onChange={(v) => update({ show_postcode: v })} testid="lf-show-postcode" />
              <Toggle label="Show message box" checked={cfg.show_message} onChange={(v) => update({ show_message: v })} testid="lf-show-message" />
              <Toggle label='Show "What do you need?" service options' checked={cfg.show_services} onChange={(v) => update({ show_services: v })} testid="lf-show-services" />
            </Card>

            {cfg.show_services && (
              <ServicesEditor services={cfg.services} onChange={(services) => update({ services })} />
            )}

            <div className="flex gap-3">
              <Button onClick={save} disabled={saving || !dirty} className="flex-1 h-11 bg-[#006400] hover:bg-[#004d00] text-white" data-testid="lf-save">
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </Button>
            </div>
          </div>

          <div className="space-y-5">
            <Card className="p-5">
              <div className="flex items-center justify-between gap-2 mb-2">
                <h2 className="font-bold text-slate-800">Embed code</h2>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={regenerate} data-testid="lf-regenerate"><RefreshCw className="w-3.5 h-3.5 mr-1" /> New link</Button>
                  <Button size="sm" onClick={copy} className="bg-[#27292E] text-white" data-testid="lf-copy">
                    {copied ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-2">Paste this anywhere in your website's page HTML (Wix, Squarespace, WordPress “Custom HTML” block, etc.). Changes you save here update the live form automatically — no need to re-paste.</p>
              <pre className="text-[11px] sm:text-xs text-slate-700 bg-slate-50 border border-slate-200 p-3 rounded-md overflow-auto whitespace-pre-wrap break-all m-0" data-testid="lf-snippet">{snippet}</pre>
              <p className="text-xs text-slate-500 mt-2">Direct link: <a href={url} target="_blank" rel="noreferrer" className="text-[#006400] underline" data-testid="lf-direct-link">{url}</a></p>
            </Card>

            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500 font-bold mb-2">Live preview</div>
              <InstallerLeadFormView key={JSON.stringify([cfg.show_services, cfg.show_message, cfg.show_postcode])} config={cfg} preview onSubmit={async () => {}} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Text({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testid} />
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </label>
  );
}

function ServicesEditor({ services, onChange }) {
  const [newLabel, setNewLabel] = useState('');
  const setAt = (i, patch) => onChange(services.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const add = () => {
    const label = newLabel.trim();
    if (!label) return;
    const id = `${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 30)}_${Date.now().toString(36).slice(-4)}`;
    onChange([...services, { id, label, sector: 'general', enabled: true }]);
    setNewLabel('');
  };
  return (
    <Card className="p-5 space-y-3" data-testid="lf-services-editor">
      <div>
        <h2 className="font-bold text-slate-800">Service options</h2>
        <p className="text-xs text-slate-500">Each option is tagged with the CRM sector the lead will be filed under. Add your own — landscaping, fencing, anything you offer.</p>
      </div>
      <div className="space-y-2">
        {services.map((s, i) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 border rounded-lg p-2 bg-white" data-testid={`lf-service-row-${s.id}`}>
            <Switch checked={s.enabled !== false} onCheckedChange={(v) => setAt(i, { enabled: v })} data-testid={`lf-service-enabled-${s.id}`} />
            <Input value={s.label} onChange={(e) => setAt(i, { label: e.target.value })} className="flex-1 min-w-[140px] h-9" data-testid={`lf-service-label-${s.id}`} />
            <select value={s.sector} onChange={(e) => setAt(i, { sector: e.target.value })} className="h-9 border rounded-md px-2 text-sm bg-white" data-testid={`lf-service-sector-${s.id}`}>
              {LEAD_SECTORS.map((sec) => <option key={sec.id} value={sec.id}>{sec.label}</option>)}
            </select>
            <button type="button" onClick={() => onChange(services.filter((_, idx) => idx !== i))} className="p-2 text-slate-400 hover:text-red-600" aria-label="Remove" data-testid={`lf-service-remove-${s.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} placeholder="e.g. Landscaping" className="h-9" data-testid="lf-service-new-label" />
        <Button type="button" size="sm" variant="outline" onClick={add} data-testid="lf-service-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </div>
    </Card>
  );
}
